import { useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import type { WholesaleRecord, RetailRecord, DataSource, UnitMode } from './types';
import {
  parseUSDate,
  fmtDate,
  fmtWeek,
  shortMarket,
  wholesalePriceInUnit,
  retailPriceInUnit,
} from './utils';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface DataTableTabProps {
  wholesaleData: WholesaleRecord[];
  retailData: RetailRecord[];
  dataSource: DataSource;
  unit: UnitMode;
  commodity: string;
}

const DataTableTab = ({ wholesaleData, retailData, dataSource, unit, commodity }: DataTableTabProps) => {
  const { t } = useTranslation('usda-market');
  const unitLabel = unit === 'kg' ? '$/kg' : '$/lb';

  const sortedWholesale = useMemo(() => {
    if (dataSource === 'retail') return [];
    return [...wholesaleData]
      .sort((a, b) => parseUSDate(b.report_date).getTime() - parseUSDate(a.report_date).getTime())
      .slice(0, 500);
  }, [wholesaleData, dataSource]);

  const sortedRetail = useMemo(() => {
    if (dataSource !== 'retail') return [];
    return [...retailData]
      .sort((a, b) => parseUSDate(b.report_begin_date).getTime() - parseUSDate(a.report_begin_date).getTime())
      .slice(0, 500);
  }, [retailData, dataSource]);

  if (dataSource !== 'retail') {
    const total = wholesaleData.length;
    return (
      <Card className='mt-4'>
        <CardHeader className='pb-0'>
          <h3 className='text-lg font-semibold'>
            {dataSource === 'wholesale' ? 'Wholesale (Terminal)' : 'FOB (Shipping Point)'} — {total} records
          </h3>
        </CardHeader>
        <CardBody>
          <Table aria-label='wholesale data' isStriped isCompact classNames={{ wrapper: 'max-h-[600px] overflow-auto' }}>
            <TableHeader>
              <TableColumn>{t('FECHA')}</TableColumn>
              <TableColumn>{t('MERCADO')}</TableColumn>
              <TableColumn>{t('VARIEDAD')}</TableColumn>
              <TableColumn>{t('ORIGEN')}</TableColumn>
              <TableColumn>{t('PAQUETE')}</TableColumn>
              <TableColumn>{t('TAMAÑO')}</TableColumn>
              <TableColumn>{t('BAJO')}</TableColumn>
              <TableColumn>{t('ALTO')}</TableColumn>
              <TableColumn>{unitLabel}</TableColumn>
              <TableColumn>{t('MAYORMENTE')}</TableColumn>
              <TableColumn>{t('TONO')}</TableColumn>
            </TableHeader>
            <TableBody>
              {sortedWholesale.map((r, i) => {
                const pUnit = wholesalePriceInUnit(r, unit);
                return (
                  <TableRow key={i}>
                    <TableCell className='text-xs whitespace-nowrap'>{fmtDate(r.report_date)}</TableCell>
                    <TableCell className='text-xs'>{shortMarket(r.market_location_name || '')}</TableCell>
                    <TableCell className='text-xs'>{r.variety}</TableCell>
                    <TableCell className='text-xs'>{r.origin}</TableCell>
                    <TableCell className='text-xs'>{r.package}</TableCell>
                    <TableCell className='text-xs'>{r.item_size}</TableCell>
                    <TableCell className='text-xs font-medium'>${r.low_price}</TableCell>
                    <TableCell className='text-xs font-medium'>${r.high_price}</TableCell>
                    <TableCell className='text-xs font-semibold text-orange-600'>
                      {pUnit != null ? `$${pUnit.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell className='text-xs'>
                      {r.mostly_low_price ? `$${r.mostly_low_price}–$${r.mostly_high_price}` : '—'}
                    </TableCell>
                    <TableCell className='text-xs'>{r.market_tone_comments}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {total > 500 && (
            <p className='text-xs text-default-400 mt-2 text-center'>{t('Mostrando primeros', { count: 500, total })}</p>
          )}
        </CardBody>
      </Card>
    );
  }

  // Retail table
  const total = retailData.length;
  return (
    <Card className='mt-4'>
      <CardHeader className='pb-0'>
        <h3 className='text-lg font-semibold'>Retail — {total} records</h3>
      </CardHeader>
      <CardBody>
        <Table aria-label='retail data' isStriped isCompact classNames={{ wrapper: 'max-h-[600px] overflow-auto' }}>
          <TableHeader>
            <TableColumn>{t('SEMANA')}</TableColumn>
            <TableColumn>{t('REGIÓN')}</TableColumn>
            <TableColumn>{t('VARIEDAD')}</TableColumn>
            <TableColumn>{t('TAMAÑO')}</TableColumn>
            <TableColumn>{t('ORGÁNICO')}</TableColumn>
            <TableColumn>{t('TIENDAS')}</TableColumn>
            <TableColumn>{t('PROM POND')}</TableColumn>
            <TableColumn>{unitLabel}</TableColumn>
            <TableColumn>{t('SEM ANT')}</TableColumn>
            <TableColumn>{t('AÑO ANT')}</TableColumn>
            <TableColumn>{t('SEM/SEM')}</TableColumn>
            <TableColumn>{t('AÑO/AÑO')}</TableColumn>
          </TableHeader>
          <TableBody>
            {sortedRetail.map((r, i) => {
              const pUnit = retailPriceInUnit(r, unit, commodity);
              const wkChg =
                r.wtd_avg_price && r.prior_WK_wtd_avg_price
                  ? ((r.wtd_avg_price - r.prior_WK_wtd_avg_price) / r.prior_WK_wtd_avg_price) * 100
                  : null;
              const yrChg =
                r.wtd_avg_price && r.prior_YR_wtd_avg_price
                  ? ((r.wtd_avg_price - r.prior_YR_wtd_avg_price) / r.prior_YR_wtd_avg_price) * 100
                  : null;
              return (
                <TableRow key={i}>
                  <TableCell className='text-xs whitespace-nowrap'>
                    {fmtWeek(r.report_begin_date, r.report_end_date)}
                  </TableCell>
                  <TableCell className='text-xs'>{r.region}</TableCell>
                  <TableCell className='text-xs'>{r.variety}</TableCell>
                  <TableCell className='text-xs'>{r.size}</TableCell>
                  <TableCell className='text-xs'>{r.organic}</TableCell>
                  <TableCell className='text-xs'>{r.store_count ?? '—'}</TableCell>
                  <TableCell className='text-xs font-medium'>
                    {r.wtd_avg_price != null ? `$${r.wtd_avg_price.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell className='text-xs font-semibold text-orange-600'>
                    {pUnit != null ? `$${pUnit.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell className='text-xs'>
                    {r.prior_WK_wtd_avg_price != null ? `$${r.prior_WK_wtd_avg_price.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell className='text-xs'>
                    {r.prior_YR_wtd_avg_price != null ? `$${r.prior_YR_wtd_avg_price.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell className={`text-xs font-medium ${wkChg != null ? (wkChg >= 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                    {wkChg != null ? `${wkChg >= 0 ? '+' : ''}${wkChg.toFixed(1)}%` : '—'}
                  </TableCell>
                  <TableCell className={`text-xs font-medium ${yrChg != null ? (yrChg >= 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                    {yrChg != null ? `${yrChg >= 0 ? '+' : ''}${yrChg.toFixed(1)}%` : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {total > 500 && (
          <p className='text-xs text-default-400 mt-2 text-center'>{t('Mostrando primeros', { count: 500, total })}</p>
        )}
      </CardBody>
    </Card>
  );
};

export default DataTableTab;
